import styled from '@emotion/styled';
import { SmallText } from 'ds/components/typography';
import { Chip } from 'screens/Investors/components/RealEstateCard/styled';
import { MAX_CONTENT_WIDTH } from 'screens/Investors/HomePage/const';

// top:108px assumes sitting directly under the legacy header (60px) + Subheader (48px) stack —
// the only context this component is rendered in today (Subheader.tsx). sticky=false (used when
// this is dropped in as an ordinary in-flow v2 page section instead) drops the sticky/offset
// entirely rather than guessing a v2-equivalent offset that has no equivalent global stacking.
export const StickyRoot = styled.div<{ sticky?: boolean }>`
  position: ${({ sticky = true }) => sticky ? 'sticky' : 'static'};
  top: ${({ sticky = true }) => sticky ? '108px' : 'auto'};
  z-index: 14;
  width: 100%;
`;

export const Bar = styled.div`
  position: relative;
  width: 100%;
  height: 56px;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.neutrals.grey10};
`;

export const MarqueeSlot = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
`;

export const SpinnerSlot = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Toggle = styled.button`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  // no left padding: the Divider (last child → physical-left in RTL) must sit flush against the
  // toggle's opaque left edge so the scrolling text is masked exactly at the visible divider line,
  // not ~16px earlier in blank grey. Right padding keeps the label off the screen edge.
  padding: 0 ${({ theme }) => theme.spacing(2)} 0 0;
  background-color: ${({ theme }) => theme.colors.neutrals.grey10};
  border: none;
  cursor: pointer;
  font-family: inherit;
`;

// icon + label sit tight together (Figma node 11087:95649: gap-[4px]); the Toggle's own larger gap
// separates this cluster from the Divider that fences it off from the scrolling ticker text. In
// RTL the label is the first (physical-right) child and the icon lands to its physical left,
// matching Figma's [icon][label] reading order.
export const CtaContent = styled.span`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.5)};
`;

// collapsed/expanded is a genuine icon swap (outlined down-circle ↔ filled up-circle), not a
// rotation — see the Figma toggle component's Default/Variant2 states.
export const ToggleIcon = styled.span`
  display: flex;
  align-items: center;
  & svg {
    display: block;
  }
`;

// Figma small_text_bold (node 11087:95629): 14px / 20px / bold — SmallText's own default is 13px/500.
export const ToggleText = styled(SmallText)`
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutrals.grey1};
  white-space: nowrap;
`;

export const Divider = styled.span`
  width: 1px;
  height: 32px;
  background-color: ${({ theme }) => theme.colors.neutrals.grey6};
`;

// the shared Marquee forces direction:ltr on its root (load-bearing: the translateX(-50%) loop
// anchors the strip to the left edge, so the item *sequence* stays LTR). This item's internal
// parts, however, are authored in RTL reading order (see TickerItem.tsx) — restore direction:rtl
// here, below the animated flex row, so each item reads right-to-left without disturbing the loop.
export const TickerItemWrapper = styled(SmallText)`
  direction: rtl;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

// Figma ticker text is small_text (14px/20px) — the ds SmallText defaults to the legacy 13px,
// so the bar's title / value / period text is bumped to 14px here (the Bullet keeps its own
// 15px Bold/Big).
export const TickerText = styled(SmallText)`
  font-size: 14px;
  line-height: 20px;
`;

// Figma ticker trend badge is small_text_bold (14px/700); the shared Chip renders 13px/400.
// Scoped override so the expandable panel's RealEstateCard chips keep their original size.
export const TickerChip = styled(Chip)`
  font-size: 14px;
  font-weight: 700;
`;

// the value+description pair sits closer together (4px) than the rest of the item's own 8px gap
// — confirmed via Figma (node 11156:54476: outer cluster gap-[8px], inner value/description
// subframe gap-[4px])
export const ValueDescGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.5)};
`;

// confirmed via Figma (node 11156:54476): the "•" separator is its own bolder, larger text style
// ("Bold/Big": 15px/24px bold) — distinct from the surrounding 14px regular text, not just a plain
// SmallText character like the rest of the row
export const Bullet = styled.span`
  color: ${({ theme }) => theme.colors.neutrals.grey1};
  font-weight: 700;
  font-family: NovemberHebrew;
  font-size: 15px;
  line-height: 24px;
`;

// Figma: a uniform 24px gap on each side of the divider between params (node 11179:100438
// gap-[24px]). The divider is the last child of TickerItemWrapper (flex gap: 8px), so its
// leading (right, RTL) side already gets 8px from that gap → 16px here makes 24px; the trailing
// (left) side abuts the next marquee sibling with no shared gap → the full 24px lives here.
export const TickerDivider = styled.span`
  width: 1px;
  height: 32px;
  background-color: ${({ theme }) => theme.colors.neutrals.grey6};
  margin-right: ${({ theme }) => theme.spacing(2)};
  margin-left: ${({ theme }) => theme.spacing(3)};
`;

export const Panel = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  width: 100%;
  overflow: hidden;
  padding: 32px 24px;
  background-color: ${({ theme }) => theme.colors.neutrals.grey10};
  box-shadow: ${({ theme }) => theme.shadow.level3};
  border-bottom-left-radius: 64px;
  border-bottom-right-radius: 64px;
  animation: marketTickerExpand 0.25s ease;

  @keyframes marketTickerExpand {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  ${({ theme }) => theme.media.showAt({ to: 2 })`
    padding: 16px;
    // mobile inverts the surface colors (Figma node 11164-94828): grey panel, white cards/chart.
    background-color: ${theme.colors.neutrals.grey10};
    box-shadow: ${theme.shadow.level2};
    border-bottom-left-radius: 24px;
    border-bottom-right-radius: 24px;
  `}
`;

export const PanelInner = styled.div<{ contentWidth?: number }>`
  max-width: ${({ contentWidth = MAX_CONTENT_WIDTH }) => contentWidth}px;
  margin: 0 auto;
  padding: 24px;
  background-color: ${({ theme }) => theme.colors.neutrals.white};
  border: 1px solid #ececec;
  border-radius: 48px;

  ${({ theme }) => theme.media.showAt({ to: 2 })`
    padding: 0;
    border: none;
    border-radius: 0;
    // mobile: no white container — the grey Panel shows through so the white cards/chart read as
    // distinct surfaces (Figma node 11164-94828).
    background-color: transparent;
    max-height: 70vh;
    overflow-y: auto;
  `}
`;

export const DataRow = styled.div`
  display: flex;
  gap: 48px;
  align-items: stretch;
  ${({ theme }) => theme.media.showAt({ to: 2 })`
    flex-direction: column;
    gap: 16px;
  `}
`;

export const InfoPart = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  width: 404px;
  flex-shrink: 0;
  ${({ theme }) => theme.media.showAt({ to: 2 })`
    width: 100%;
  `}
`;

export const VisualPart = styled.div`
  width: 100%;
  & .highcharts-legend-item {
    line-height: 20px;
  }
  // mobile: the chart becomes its own white card on the grey panel (Figma node 11164-94828).
  ${({ theme }) => theme.media.showAt({ to: 2 })`
    background-color: ${theme.colors.neutrals.white};
    border: 1px solid ${theme.colors.neutrals.grey8};
    border-radius: 24px;
    padding: 8px 16px 16px;
  `}
`;

export const CardsRow = styled.div`
  display: flex;
  gap: 8px;
  & > * {
    width: 50%;
  }
`;
